import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import ReactJWPlayer from 'react-jw-player';
import { Movie } from 'src/@types/Movie';
import { JoinButton } from 'src/client/components/buttons/JoinButton';
import { BlurrableImage } from 'src/client/components/ui/BlurredImage';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { useSelector } from 'src/client/redux';
import styled from 'styled-components/macro';
import { AccessMode, getJWPlayerOptions, getMovieCover, useGenerateJWPlayerEvents, Videos } from './movieUtils';

type Props = {
  accessMode: AccessMode;
  contentCdnUrl: string;
  item: Movie;
  showPlayer: boolean;
  token: string;
  videos: Videos;
};

export const MoviePlayerContainer = ({ accessMode, contentCdnUrl, item, showPlayer, token, videos }: Props) => {
  const showSfwSite = useShowSfwSite();
  const { cdnUrl, jwPlayerKey } = useSelector((state) => state.app);
  const [skipM3U8, setSkipM3U8] = useState(false);

  const generateEvents = useGenerateJWPlayerEvents({ accessMode, item, skipM3U8, setSkipM3U8 });

  if (!showPlayer) {
    return null;
  }

  // We don't have any way to show the non-nude trailers yet, so if we have the
  // flag to show the non-nude site I'm just bypassing the entire movie player
  if (showSfwSite) {
    return <NonNudeMoviePlaceholder item={item} />;
  }

  const playlist = getJWPlayerOptions({
    item,
    accessMode,
    skipM3U8,
    token,
    videos,
    contentCdnUrl,
    showSfwSite,
  });

  const playerConfig = {
    cast: {
      enabled: false,
    },
  };

  const playerEvents = generateEvents();

  return (
    <ReactJWPlayer
      playerId="my-unique-id"
      playerScript={`${cdnUrl}/jwplayer/jwplayer.js`}
      playlist={playlist}
      licenseKey={jwPlayerKey}
      aspectRatio="16:9"
      playerConfig={playerConfig}
      customProps={{ width: '100%' }}
      {...playerEvents}
    />
  );
};

const NonNudeMoviePlaceholder = ({ item }: { item: Movie }) => {
  const { contentCdnUrl } = useSelector((state) => state.app);
  const [movieCover, isSafe] = getMovieCover({ showSfwSite: true, item, contentCdnUrl });
  const [showOverlay, setShowOverlay] = useState(false);

  return (
    <ImageWrapper>
      <StyledImage src={movieCover} alt={item.description} $isBlurred={Boolean(movieCover && !isSafe)} />
      <StyledPlayIcon icon={faPlay} size="5x" onClick={() => setShowOverlay(true)} />
      {showOverlay && (
        <MembersOnlyOverlay>
          Members Only
          <StyledJoinButton campaign="non-nude-video">Watch the Full Video Here</StyledJoinButton>
        </MembersOnlyOverlay>
      )}
    </ImageWrapper>
  );
};

const StyledImage = styled(BlurrableImage)`
  width: 100%;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;

  img {
    width: 100%;
  }
`;

const StyledPlayIcon = styled(FontAwesomeIcon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 2;
`;

const MembersOnlyOverlay = styled.div`
  text-transform: uppercase;
  position: absolute;
  top: 50%;
  left: 50%;
  background-color: black;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  padding: 20px;
  transform: translate(-50%, -50%);
  z-index: 3;
`;

const StyledJoinButton = styled(JoinButton).attrs({
  className: 'btn btn-primary join-btn',
})`
  background: ${(props) => props.theme.headerBarPrimary3} !important;
  color: ${(props) => props.theme.primaryBtn1Text} !important;
  opacity: 1;
  margin-top: 10px;
  font-size: 1.5rem;

  &:hover {
    opacity: 0.9;
  }
`;
